import { useCallback, useEffect, useState } from 'react';
import { Modal, Box, Button, Typography } from '@mui/material';

import { Loading, ErrorMessage } from '@/src/ui';
import { apiRequest } from '@/src/services';
import { Constants, getTextColorFromBackground, hexToRgba } from '@/src/utils';
import type { AssessmentHistoryExamBandDTO } from '../models/assessmentHistoryDTOs';
import type { AssessmentHistoryFilterState } from '../models/assessmentHistoryFilterDTOs';

interface ExamBandsModalProps {
    open: boolean;
    onClose: () => void;
    examId: number;
    examName: string;
    filterState: AssessmentHistoryFilterState;
}

const bandOrder = ['Not Met', 'Below', 'Near', 'Nearly', 'At/Above', 'Met', 'Exceeds'];

function formatRange(from: number | null, to: number | null) {
    if (from == null && to == null) return '';
    if (from != null && to != null) return `${from}% – ${to}%`;
    if (from != null) return `≥ ${from}%`;
    return `≤ ${to}%`;
}

export default function ExamBandsModal({
    open,
    onClose,
    examId,
    examName,
    filterState,
}: ExamBandsModalProps) {
    const [data, setData] = useState<AssessmentHistoryExamBandDTO[] | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const fetchBands = useCallback(async () => {
        setError('');
        setLoading(true);
        try {
            const params = new URLSearchParams({
                districtId: String(filterState.districtId),
                schoolId: String(filterState.schoolId),
                gradeId: String(filterState.gradeLevelMasterId),
                examId: String(examId),
            });
            if (filterState.classId) params.append('classId', String(filterState.classId));
            const res = await apiRequest<AssessmentHistoryExamBandDTO[]>(
                'GET',
                `reports/assessmentHistory/examBands?${params.toString()}`
            );
            setData(res.data);
        } catch {
            setError('Failed to load exam bands');
        } finally {
            setLoading(false);
        }
    }, [examId, filterState]);

    useEffect(() => {
        if (!open) return;
        setData(null);
        fetchBands();
    }, [open, fetchBands]);

    const sortedBands = data
        ? [...data].sort((a, b) => {
              const ai = bandOrder.indexOf(a.name);
              const bi = bandOrder.indexOf(b.name);
              return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
          })
        : [];

    const totalStudents = sortedBands.reduce((sum, b) => sum + b.students.length, 0);

    return (
        <Modal open={open} onClose={onClose}>
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    boxShadow: 24,
                    p: 3,
                    width: '85%',
                    maxWidth: 1200,
                    maxHeight: '85vh',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                <Typography variant="h6" fontWeight="bold" mb={1}>
                    Band Distribution: {examName}
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                    Total students: <strong>{totalStudents}</strong>
                </Typography>

                <Box sx={{ position: 'relative', flex: 1, overflow: 'auto' }}>
                    <Loading visible={loading} message="Loading exam bands..." />
                    <ErrorMessage error={error} />

                    {data && !loading && !error && (
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: `repeat(${Math.max(sortedBands.length, 1)}, minmax(0, 1fr))`,
                                gap: 2,
                            }}
                        >
                            {sortedBands.map((band) => {
                                const color = Constants.bandColors[band.name] || '#888';
                                const pillTextColor = getTextColorFromBackground(color);
                                const count = band.students.length;
                                const percent =
                                    totalStudents > 0
                                        ? Math.round((count / totalStudents) * 100)
                                        : 0;
                                const range = formatRange(band.fromPercentage, band.toPercentage);

                                return (
                                    <Box
                                        key={band.name}
                                        sx={{
                                            borderRadius: 2,
                                            overflow: 'hidden',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            minWidth: 0,
                                            bgcolor: '#fff',
                                            border: '1px solid #e0e0e0',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                                        }}
                                    >
                                        <Box sx={{ height: 6, background: color }} />
                                        <Box
                                            sx={{
                                                px: 1.5,
                                                pt: 1.25,
                                                pb: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                flexWrap: 'wrap',
                                                rowGap: 0.5,
                                                columnGap: 1,
                                                borderBottom: '1px solid #eee',
                                            }}
                                        >
                                            <Box sx={{ minWidth: 0 }}>
                                                <Box
                                                    sx={{
                                                        display: 'inline-block',
                                                        background: color,
                                                        color: pillTextColor,
                                                        px: 1,
                                                        py: 0.25,
                                                        borderRadius: 1,
                                                        fontSize: '0.8125rem',
                                                        fontWeight: 700,
                                                        lineHeight: 1.3,
                                                    }}
                                                >
                                                    {band.name}
                                                </Box>
                                                {range && (
                                                    <Typography
                                                        variant="caption"
                                                        display="block"
                                                        color="text.secondary"
                                                        sx={{ mt: 0.5 }}
                                                    >
                                                        {range}
                                                    </Typography>
                                                )}
                                            </Box>
                                            <Box
                                                sx={{
                                                    flexShrink: 0,
                                                    display: 'flex',
                                                    alignItems: 'baseline',
                                                    gap: 0.75,
                                                    ml: 'auto',
                                                }}
                                            >
                                                <Typography
                                                    variant="h6"
                                                    fontWeight="bold"
                                                    sx={{ color: '#222' }}
                                                >
                                                    {count}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    students ({percent}%)
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box
                                            sx={{
                                                p: 1.5,
                                                overflowY: 'auto',
                                                maxHeight: '50vh',
                                                fontSize: '0.875rem',
                                                color: '#222',
                                                background: hexToRgba(color, 0.06),
                                            }}
                                        >
                                            {band.students.length === 0 ? (
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    fontStyle="italic"
                                                >
                                                    No students
                                                </Typography>
                                            ) : (
                                                [...band.students]
                                                    .sort((a, b) => a.name.localeCompare(b.name))
                                                    .map((s) => (
                                                        <div
                                                            key={s.id}
                                                            style={{ padding: '2px 0' }}
                                                        >
                                                            {s.name}
                                                        </div>
                                                    ))
                                            )}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </Box>

                <Box textAlign="right" mt={2}>
                    <Button variant="outlined" onClick={onClose}>
                        Close
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
}
